// Category filter for a CMS list, replacing Finsweet Attributes "List Filter" (fs-list-*) on
// the Partners page: radio buttons choose a category, the list fades out (the wrapper gets
// `is-list-filtering` for its 200ms CSS transition), non-matching items are removed, and the
// list fades back in. The result count updates immediately.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { InputHTMLAttributes } from 'react';
import { cx, type WfProps, withoutClass } from '../webflow/element';

const FADE_MS = 200;

interface FilterState {
  /** The selected value ('' = all), applied to the inputs and the count right away. */
  selected: string;
  /** The value applied to the list, which lags `selected` while the list fades out. */
  applied: string;
  filtering: boolean;
  select: (value: string) => void;
  /** Matching items, or null until the items have registered (i.e. before hydration). */
  count: number | null;
  registerItem: (values: string[]) => () => void;
}

const FilterContext = createContext<FilterState | null>(null);

const matches = (values: string[], selected: string) =>
  selected === '' || values.some((v) => v.trim().toLowerCase() === selected.trim().toLowerCase());

export function ListFilterScope({ as: Tag = 'div', initial = '', children, ...props }: WfProps & { initial?: string }) {
  const [selected, setSelected] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [filtering, setFiltering] = useState(false);
  const [items, setItems] = useState<string[][]>([]);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const select = (value: string) => {
    setSelected(value);
    setFiltering(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setApplied(value);
      setFiltering(false);
    }, FADE_MS);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const registerItem = useCallback((values: string[]) => {
    setItems((prev) => [...prev, values]);
    return () => setItems((prev) => prev.filter((v) => v !== values));
  }, []);

  return (
    <FilterContext.Provider
      value={{
        selected,
        applied,
        filtering,
        select,
        count: items.length ? items.filter((v) => matches(v, selected)).length : null,
        registerItem,
      }}
    >
      <Tag {...props}>{children}</Tag>
    </FilterContext.Provider>
  );
}

/** A filter radio button; `value` is the category it selects ('' for all). */
export function ListFilterRadio({
  filterValue,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { filterValue: string }) {
  const f = useContext(FilterContext);
  return (
    <input {...props} checked={f ? f.selected === filterValue : undefined} onChange={() => f?.select(filterValue)} />
  );
}

/** The label around a filter radio; gets `is-list-active` while its value is selected. */
export function ListFilterLabel({
  as: Tag = 'label',
  filterValue,
  className,
  children,
  ...props
}: WfProps & { filterValue: string }) {
  const f = useContext(FilterContext);
  return (
    <Tag
      {...props}
      className={cx(
        f ? withoutClass(className, 'is-list-active') : className,
        f?.selected === filterValue && 'is-list-active',
      )}
    >
      {children}
    </Tag>
  );
}

/** The list wrapper, which fades while the filter changes. */
export function ListFilterWrapper({
  as: Tag = 'div',
  className,
  children,
  ...props
}: WfProps & { 'fs-list-filteringclass'?: string }) {
  const f = useContext(FilterContext);
  return (
    <Tag {...props} className={cx(className, f?.filtering && props['fs-list-filteringclass'])}>
      {children}
    </Tag>
  );
}

/** A list item, rendered only when one of its category `values` matches the filter. */
export function ListFilterItem({ as: Tag = 'div', values, children, ...props }: WfProps & { values: string[] }) {
  const f = useContext(FilterContext);
  const registerItem = f?.registerItem;
  useEffect(() => registerItem?.(values), [registerItem, values]);
  if (f && !matches(values, f.applied)) return null;
  return <Tag {...props}>{children}</Tag>;
}

/** The "Showing N partners" count. */
export function ListFilterCount({ as: Tag = 'span', total, ...props }: WfProps & { total: number }) {
  const f = useContext(FilterContext);
  // Before hydration (and without JS) the full list is shown.
  return <Tag {...props}>{f?.count ?? total}</Tag>;
}
